import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import type { Row } from '../lib/db-types'

/**
 * The subset of `templates` we actually render. Full row type is
 * `Row<'templates'>` but we don't need `user_id`/`created_at`/`updated_at`
 * in the UI — narrowing keeps memory down and the TanStack Query cache
 * serialisations small.
 *
 * `team_id` decides scope: personal team = private, shared team = visible
 * to all teammates. Grouping happens in Templates.tsx against
 * `usePersonalTeam()` / `useSharedTeam()`.
 */
export type Template = Pick<
  Row<'templates'>,
  'id' | 'name' | 'body' | 'sort_order' | 'team_id'
>

export const templatesKey = (userId: string | undefined): QueryKey =>
  ['templates', userId ?? 'anon']

/**
 * Returns every template the user can see — their personal ones AND any
 * shared team's ones — as a flat list. RLS on `templates` enforces the
 * "member of the team_id" rule so a user only sees templates from teams
 * they belong to. Callers decide how to group.
 *
 * Error contract: mutation functions THROW on Supabase error (matching
 * useBookmarks/useReminders/useTags). Callers `await` and can try/catch
 * for toast surfacing. The previous `{data, error}` return shape silently
 * swallowed failures whenever callers didn't destructure — see PR 2 of
 * the FULL_CODE_REVIEW_HANDOFF plan.
 */
export function useTemplates() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const key = templatesKey(user?.id)

  const query = useQuery<Template[]>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('templates')
        .select('id, name, body, sort_order, team_id')
        .order('sort_order')
      if (error) throw new Error(error.message)
      return (data ?? []) as Template[]
    },
  })

  // Realtime invalidation is handled centrally by <RealtimeBridge />.

  const addMut = useMutation({
    mutationKey: ['templates', 'add'],
    mutationFn: async ({ name, body, teamId }: { name: string; body: string; teamId: string }) => {
      if (!user) throw new Error('Not authenticated')
      const current = qc.getQueryData<Template[]>(key) ?? []
      // Per-team sort_order — each section orders independently so appending
      // to Team doesn't push your Personal sort orders around.
      const inScope = current.filter((t) => t.team_id === teamId)
      const maxOrder = inScope.reduce((max, t) => Math.max(max, t.sort_order), -1)
      const { data, error } = await supabase
        .from('templates')
        .insert({ user_id: user.id, team_id: teamId, name, body, sort_order: maxOrder + 1 })
        .select('id, name, body, sort_order, team_id')
        .single()
      if (error) throw new Error(error.message)
      if (!data) throw new Error('Insert returned no row')
      return data as Template
    },
    onSuccess: (data) => {
      qc.setQueryData<Template[]>(key, (prev) => [...(prev ?? []), data])
    },
  })

  const updateMut = useMutation({
    mutationKey: ['templates', 'update'],
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Pick<Template, 'name' | 'body' | 'sort_order'>> }) => {
      // `updated_at` is maintained by a database trigger (set_updated_at on
      // templates). Don't write it from the client — otherwise the trigger's
      // post-commit value races with ours and we end up with an `updated_at`
      // that's slightly behind the actual last-write.
      const { error } = await supabase
        .from('templates')
        .update(updates)
        .eq('id', id)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key })
    },
  })

  /**
   * Move a template between scopes (personal ↔ team). Flips `team_id` on
   * the existing row — non-destructive for the content, but the template
   * disappears from the source scope and appears in the target.
   *
   * Used for "Share with team": caller sets targetTeamId = sharedTeam.id on
   * a personal template and it becomes visible to all teammates.
   *
   * RLS requires the caller to be either the creator or a team owner
   * (see "creator or owner can update templates"), plus a member of the
   * target team — which is implied by owning the shared team.
   */
  const moveMut = useMutation({
    mutationKey: ['templates', 'moveToTeam'],
    mutationFn: async ({ id, targetTeamId }: { id: string; targetTeamId: string }) => {
      const current = qc.getQueryData<Template[]>(key) ?? []
      // Sort to bottom of the target section so moves feel append-y (least
      // surprising to users who'll re-sort by drag later).
      const inScope = current.filter((t) => t.team_id === targetTeamId)
      const maxOrder = inScope.reduce((max, t) => Math.max(max, t.sort_order), -1)
      const { data, error } = await supabase
        .from('templates')
        .update({
          team_id: targetTeamId,
          sort_order: maxOrder + 1,
          // updated_at set by trigger — see note in updateMut above.
        })
        .eq('id', id)
        .select('id, name, body, sort_order, team_id')
        .single()
      if (error) throw new Error(error.message)
      if (!data) throw new Error('Update returned no row')
      return data as Template
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key })
    },
  })

  /**
   * Fork a team template into a personal copy owned by the caller. Creates
   * a new row — original stays intact for the rest of the team.
   *
   * We can't just re-use the existing row's name/body via a server-side
   * insert-select because RLS requires `auth.uid() = user_id` on insert
   * and we need to rewrite `user_id` + `team_id` + `sort_order` anyway.
   */
  const copyMut = useMutation({
    mutationKey: ['templates', 'copyToPersonal'],
    mutationFn: async ({ source, personalTeamId }: { source: Template; personalTeamId: string }) => {
      if (!user) throw new Error('Not authenticated')
      const current = qc.getQueryData<Template[]>(key) ?? []
      const inScope = current.filter((t) => t.team_id === personalTeamId)
      const maxOrder = inScope.reduce((max, t) => Math.max(max, t.sort_order), -1)
      const { data, error } = await supabase
        .from('templates')
        .insert({
          user_id: user.id,
          team_id: personalTeamId,
          name: source.name,
          body: source.body,
          sort_order: maxOrder + 1,
        })
        .select('id, name, body, sort_order, team_id')
        .single()
      if (error) throw new Error(error.message)
      if (!data) throw new Error('Insert returned no row')
      return data as Template
    },
    onSuccess: (data) => {
      qc.setQueryData<Template[]>(key, (prev) => [...(prev ?? []), data])
    },
  })

  const deleteMut = useMutation({
    mutationKey: ['templates', 'delete'],
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('templates').delete().eq('id', id)
      if (error) throw new Error(error.message)
      return id
    },
    onSuccess: (id) => {
      qc.setQueryData<Template[]>(key, (prev) => (prev ?? []).filter((t) => t.id !== id))
    },
  })

  return {
    templates: query.data ?? [],
    loading: !!user && query.isPending,
    error: query.error instanceof Error ? query.error.message : null,
    /**
     * `teamId` decides whether the new template is personal (pass the user's
     * personal team id) or shared (pass the shared team id). Resolves with
     * the inserted `Template`; rejects with `Error` on Supabase failure.
     */
    addTemplate: async (name: string, body: string, teamId: string): Promise<Template> =>
      addMut.mutateAsync({ name, body, teamId }),
    updateTemplate: async (id: string, updates: Partial<Pick<Template, 'name' | 'body' | 'sort_order'>>): Promise<void> => {
      await updateMut.mutateAsync({ id, updates })
    },
    deleteTemplate: async (id: string): Promise<void> => {
      await deleteMut.mutateAsync(id)
    },
    /**
     * Share a personal template with a team by flipping its `team_id`. The
     * row moves — it disappears from Personal and appears in the target
     * team's list. Resolves with the updated row; rejects on error.
     */
    moveTemplateToTeam: async (id: string, targetTeamId: string): Promise<Template> =>
      moveMut.mutateAsync({ id, targetTeamId }),
    /**
     * Copy a team template into the caller's personal scope. The original
     * stays with the team; a new row with the same name/body lands in
     * Personal. Resolves with the new row; rejects on error.
     */
    copyTemplateToPersonal: async (source: Template, personalTeamId: string): Promise<Template> =>
      copyMut.mutateAsync({ source, personalTeamId }),
  }
}
